import React, { useEffect, useState } from "react";
import HeadingBanner from "../components/HeadingBanner";
import DyslexiaScreenerClassCard from "../components/DyslexiaScreenerClassCard";
import LeftPanel from "../components/LeftPanel";
import AlpacaInfoGesture from "../components/AlpacaInfoGesture";
import ClassFilter from "../components/ClassFilter";
import { useNavigate } from "react-router-dom";
import {
  whoami,
  getClasses,
  getTeachers,
  getSchools,
  getCountryGrades,
  getTrialistsBySchool,
  getCountryId,
  getPilotBySchoolId,
  getScreeningWindowStatuses
} from "../services/APIClient";
import { Container, Row, Col } from "react-bootstrap";
import "../components/AssessmentClassCard.css";
import OrientationPrompt from "../components/OrientationPrompt";

function DyslexiaScreenerDashboard() {
  const [schoolid, setSchoolid] = useState(null);
  const [teacherid, setTeacherid] = useState(null);
  const [name, setName] = useState("");
  const [teachers, setTeachers] = useState(null);
  const [firstname, setFirstname] = useState(null);
  const [speechText, setSpeechText] = useState(null);
  const [classes, setClasses] = useState([]);
  const [filteredClasses, setFilteredClasses] = useState([]);
  const [gradeLevelOptions, setGradeLevelOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [teacherRoles, setTeacherRoles] = useState(null);
  const [trialistDetails, setTrialistDetails] = useState(null);
  const [countryId, setCountryId] = useState(null);
  const [pilotData, setPilotData] = useState(null);
  const [screeningWindowData, setScreeningWindowData] = useState(null);
  const DEFAULT_COUNTRY_ID = '07d45b79-24b1-435f-b82a-93861b23a86e';

  const [isPortrait, setIsPortrait] = useState(
    window.matchMedia("(orientation: portrait)").matches
  );

  const navigate = useNavigate();

  useEffect(() => {
    whoami().then((response) => {
      setSchoolid(response.data.stakeholder.schoolid);
      setTeacherid(response.data.stakeholder.id);
      setName(response.data.stakeholder.name);
      setTeacherRoles(response.data.stakeholder.additionalRoles);
    });
  }, []);

  useEffect(() => {
    async function fetchSchoolData() {
      try {
        const teachersResponse = await getTeachers(schoolid);
        setTeachers(teachersResponse);

        let classesResponse = await getClasses();

        // Remove duplicate classes based on their id
        classesResponse = classesResponse.filter(
          (classItem, index, self) =>
            index === self.findIndex((t) => t.id === classItem.id)
        );

        // Sort classes by the date_created field in descending order
        classesResponse.sort(
          (a, b) => new Date(b.date_created) - new Date(a.date_created)
        );
        setClasses(classesResponse);

        // Get country ID using the new API
        const countryResponse = await getCountryId(schoolid);

        if (countryResponse.countryId) {
          setCountryId(countryResponse.countryId);

          const gradesResponse = await getCountryGrades(countryResponse.countryId);
          setGradeLevelOptions([gradesResponse]);
        } else {
          console.warn("No country ID found for school:", countryResponse.message);
          setCountryId(null);
        }

        // Get roll number
        const schoolsResponse = await getSchools();
        const matchingSchool = schoolsResponse.data.find(
          (school) => school.id === schoolid
        );

      } catch (error) {
        console.error("Error fetching school data:", error);
        setCountryId(null);
      }
    }

    if (schoolid) {
      fetchSchoolData().finally(() => {
        setLoading(false);
      });
    }
  }, [schoolid]);

  useEffect(() => {
    if (name) {
      const nameArray = name.split(" ");
      setFirstname(nameArray[0]);
    }
  }, [name, schoolid]);

  useEffect(() => {
    setSpeechText(
      `Whoa slow down there ${firstname}. We need to create a class first. Click into "Classrooms" on the left to get this started`
    );
  }, [firstname]);

  useEffect(() => {
    const handleOrientationChange = () => {
      setIsPortrait(window.innerWidth < window.innerHeight);
    };

    window.addEventListener("resize", handleOrientationChange);

    return () => {
      window.removeEventListener("resize", handleOrientationChange);
    };
  }, []);

  useEffect(() => {
    window.HubSpotConversations?.widget?.refresh();
  }, []);

  useEffect(() => {
    const fetchTrialistDetails = async () => {
      try {
        const response = await getTrialistsBySchool(schoolid);
        if (response.length > 0) {
          setTrialistDetails(response[0]);
        }
      } catch (error) {
        console.error("Failed to fetch trialist details:", error);
      }
    };
    fetchTrialistDetails();
  }, [schoolid]);

  useEffect(() => {
    const fetchPilotAndScreeningData = async () => {
      if (!schoolid || !countryId) return;

      try {
        const pilotResponse = await getPilotBySchoolId(schoolid);
        setPilotData(pilotResponse);

        let screeningResponse = await getScreeningWindowStatuses({
          countryid: countryId,
          active: true
        });

        if (!screeningResponse || screeningResponse.length === 0) {
          screeningResponse = await getScreeningWindowStatuses({
            countryid: DEFAULT_COUNTRY_ID,
            active: true
          });
        }

        setScreeningWindowData(screeningResponse);
      } catch (error) {
        console.error('Error fetching pilot/screening data:', error);
      }
    };

    fetchPilotAndScreeningData();
  }, [schoolid, countryId]);

  const { purchased = false } = trialistDetails || {};

  return (
    <div>
      <Container fluid className="vh-100">
        <HeadingBanner
          name={`${name}'s Dyslexia Screener`}
          isLoggedIn={true}
          cloud={true}
          teacherid={teacherid}
          purchased={purchased}
          purchased_2425={trialistDetails?.purchased_2425}
          purchased_2526={trialistDetails?.purchased_2526}
          purchased_2627={trialistDetails?.purchased_2627}
          pilotData={pilotData}
          screeningWindowData={screeningWindowData}
        />

        <Row className="h-100">
          <LeftPanel />
          <Col sm={{ span: 10, offset: 2 }} className="blue-background">
            {loading ? (
              <div className="spinner-container">
                <div className="big-ball-spinner"></div>
              </div>
            ) : (
              <>
                <Row className="row-position">
                  <Col xs={8}>
                    <ClassFilter
                      classes={classes}
                      setFilteredClasses={setFilteredClasses}
                      gradeLevelOptions={gradeLevelOptions}
                      teacherRoles={teacherRoles}
                      showCurrentYearOnly={true}
                      schoolid={schoolid}
                    />
                  </Col>
                </Row>
                <Row className="row-position-assessment">
                  {/* Class Cards */}
                  {filteredClasses.length === 0 ? (
                    <AlpacaInfoGesture speechText={speechText} />
                  ) : (
                    filteredClasses.map((item, index) => (
                      <DyslexiaScreenerClassCard
                        key={`${item.id}-${index}`}
                        teachers={teachers}
                        classDetails={item}
                        gradeLevelOptions={gradeLevelOptions}
                        teacherid={teacherid}
                        schoolid={schoolid}
                        countryId={countryId}
                      />
                    ))
                  )}
                </Row>
              </>
            )}
          </Col>
        </Row>
      </Container>
      {isPortrait && <OrientationPrompt />}
    </div>
  );
}

export default DyslexiaScreenerDashboard;
